import isURL from "validator/lib/isURL";
import { FontOrigin, type Font } from "#gen/brands/v1/service_pb.js";
import type { ParsedFontFile } from "fontname";

export const systemFontsMap: Record<string, string> = {
	Arial: "Arial, Helvetica, sans-serif",
	Helvetica: "Helvetica, sans-serif",
	"Times New Roman": "Times New Roman, Times, serif",
	Times: "Times, serif",
	Georgia: "Georgia, serif",
	"Courier New": "Courier New, Courier, monospace",
	Courier: "Courier, monospace",
	Verdana: "Verdana, Geneva, sans-serif",
	Geneva: "Geneva, sans-serif",
};

export function fontToStyleString(font?: Font | null) {
	if (!font?.originId) return "inherit";
	const fontFamily = font.origin == FontOrigin.GOOGLE ? font.originId.replace("+", " ") : font.name;
	return `font-family: '${fontFamily}';`;
}

enum GoogleFontsSortBy {
	ALPHA = "alpha",
	DATE = "date",
	POPULARITY = "popularity",
	STYLE = "style",
	TRENDING = "trending",
}

type GoogleFontCategory = "serif" | "sans-serif" | "handwriting" | "display" | "monospace";

type GoogleFontScript = "arabic" | "bengali" | "chinese-simplified" | "chinese-traditional" | "cyrillic" | "cyrillic-ext" | "devanagari" | "greek" | "greek-ext" | "gujarati" | "gurmukhi" | "hebrew" | "japanese" | "kannada" | "khmer" | "korean" | "latin" | "latin-ext" | "malayalam" | "myanmar" | "oriya" | "sinhala" | "tamil" | "telugu" | "thai" | "vietnamese";

enum GoogleFontCapability {
	VF = "VF",
	WOFF2 = "WOFF2",
}

interface GoogleFontsResponse {
	kind: GoogleFontKind;
	items: GoogleFont[];
}

export interface GoogleFont {
	family: string;
	variants: string[];
	subsets: string[];
	version: string;
	lastModified: Date;
	files: GoogleFontFiles;
	category: GoogleFontCategory;
	kind: GoogleFontKind;
	menu: string;
}

interface GoogleFontFiles {
	regular?: string;
	italic?: string;
	"500"?: string;
	"600"?: string;
	"700"?: string;
	"800"?: string;
	"100"?: string;
	"200"?: string;
	"300"?: string;
	"900"?: string;
	"100italic"?: string;
	"200italic"?: string;
	"300italic"?: string;
	"500italic"?: string;
	"600italic"?: string;
	"700italic"?: string;
	"800italic"?: string;
	"900italic"?: string;
}

enum GoogleFontKind {
	WebfontsWebfont = "webfonts#webfont",
}

interface GoogleFontsListOptions {
	sort?: GoogleFontsSortBy;
	subset?: GoogleFontScript;
	capability?: GoogleFontCapability;
	family?: string;
	key: string;
}

export async function getGoogleFontsList(fn: typeof globalThis.fetch, options: Record<string, string> = { key: "" } satisfies GoogleFontsListOptions): Promise<GoogleFont[]> {
	const params = new URLSearchParams(options);
	const response = await fn(`https://www.googleapis.com/webfonts/v1/webfonts?${params}`);
	const data = (await response.json()) as GoogleFontsResponse;
	return data.items;
}

export function generateFontStylesheetUrls(fonts: Font[] = []): string[] {
	const googleFonts = fonts.filter(font => font.origin === FontOrigin.GOOGLE);
	const customFonts = fonts.filter(font => font.origin === FontOrigin.CUSTOM && font.originId && isURL(font.originId));

	const googleFontsParams = generateGoogleFontsParams(googleFonts);
	const googleFontStyleSheetUrl = googleFontsParams ? `https://fonts.googleapis.com/css2?${googleFontsParams}` : undefined;
	const customFontStylesheetUrls = customFonts.map(font => font.originId);

	const urls = [googleFontStyleSheetUrl, ...customFontStylesheetUrls].filter(Boolean) as string[];
	return [...new Set(urls)];
}

function generateGoogleFontsParams(fonts: Font[] = []): string | undefined {
	const googleFonts = fonts.filter(font => font.origin === FontOrigin.GOOGLE);
	if (!googleFonts.length) return undefined;

	const paramsObj = new URLSearchParams({ display: "swap" });
	googleFonts.forEach(font => {
		paramsObj.append("family", font.originId + ":wght@400;500;600");
	});
	return decodeURIComponent(paramsObj.toString());
}

export async function parseFontFile(fontFile: File) {
	const FontName = await import("fontname");
	const reader = new FileReader();
	return new Promise<ParsedFontFile>((resolve, reject) => {
		reader.onload = () => {
			const buffer = reader.result as ArrayBuffer;
			const parsed = FontName.parse(buffer);
			resolve(parsed[0]);
		};
		reader.onerror = reject;
		reader.readAsArrayBuffer(fontFile);
	});
}

export const fontFormatMap: Record<string, string> = {
	woff: "woff",
	woff2: "woff2",
	otf: "opentype",
	ttf: "truetype",
	eot: "embedded-opentype",
};

export const fontMimeTypes: Record<string, string> = {
	ttf: "font/ttf",
	otf: "font/otf",
	woff: "font/woff",
	woff2: "font/woff2",
	eot: "application/vnd.ms-fontobject",
};
